import React, { useState } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';

interface SaveMasterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: { name: string; updateContent: boolean }) => void;
    defaultName: string;
    clipType: 'synthe' | 'panel';
}

export const SaveMasterModal: React.FC<SaveMasterModalProps> = ({
    isOpen,
    onClose,
    onSave,
    defaultName,
    clipType
}) => {
    const [name, setName] = useState(defaultName);
    const [updateContent, setUpdateContent] = useState(false);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave({ name, updateContent });
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-md mx-4 bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-white/10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                    <h2 className="text-lg font-semibold text-white">
                        {clipType === 'synthe' ? 'Sauvegarder le Synthé' : 'Sauvegarder le Panel'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-5">
                    {/* Name Input */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-white/70">Nom</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Nom du template..."
                            className="w-full px-4 py-3 bg-black/40 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all"
                        />
                    </div>

                    {/* Update Content Option */}
                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                                <RefreshCw className="w-5 h-5 text-blue-400" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-sm font-medium text-white">Mettre à jour le contenu</h3>
                                <p className="text-xs text-white/50 mt-1">
                                    Remplacer le contenu du modèle par les réglages de l'élément actuellement sélectionné dans la timeline.
                                </p>
                                <label className="flex items-center gap-2 mt-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={updateContent}
                                        onChange={(e) => setUpdateContent(e.target.checked)}
                                        className="w-4 h-4 rounded border-white/30 bg-black/40 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                                    />
                                    <span className="text-sm text-white/70 group-hover:text-white transition-colors">
                                        Oui, mettre à jour le Master
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10 bg-black/20">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!name.trim()}
                        className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-4 h-4" />
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SaveMasterModal;
